import { Box, Center, Flex, Stack, Text } from '@chakra-ui/react';
import { GroupMemberRole, type GroupMember } from '@halo/chat-sdk';
import { AddIcon, Card, RemoveIcon, UserItem } from '@halo/ui-chat-react';
import { StateEnum } from '@halo/chat-sdk-react';
import { useTranslation } from 'react-i18next';
import { MembersList } from './MembersList';

interface MembersCardProps {
  members: GroupMember[];
  isGroupManager?: boolean;
  hideMember?: boolean;
  closeAllClick?: () => void;
  onAddMembers: () => void;
  onRemoveMembers: () => void;
  onMemberAvatarClick?: (member: GroupMember) => Promise<void>;
}

const MembersCard = ({
  members: originalMembers,
  isGroupManager,
  hideMember,
  closeAllClick,
  onAddMembers,
  onRemoveMembers,
  onMemberAvatarClick,
}: MembersCardProps) => {
  const { t } = useTranslation();

  const hideMore = useMemo(() => {
    return hideMember && !isGroupManager;
  }, [hideMember, isGroupManager]);

  const showMembers = useMemo(() => {
    const sortByState = (a: GroupMember, b: GroupMember) => {
      if (a.state === StateEnum.DELETE_ACCOUNT && b.state !== StateEnum.DELETE_ACCOUNT) return 1;
      if (a.state !== StateEnum.DELETE_ACCOUNT && b.state === StateEnum.DELETE_ACCOUNT) return -1;
      return 0;
    };

    const sortByRole = (a: GroupMember, b: GroupMember) => {
      return a.role - b.role;
    };

    const sortedMembers = [...originalMembers].sort((a, b) => {
      const stateDiff = sortByState(a, b);
      if (stateDiff !== 0) return stateDiff;
      return sortByRole(a, b);
    });

    if (hideMember && !isGroupManager) {
      return sortedMembers.filter((member) => member.role < GroupMemberRole.MEMBER).slice(0, 3);
    }

    return sortedMembers.slice(0, 3);
  }, [hideMember, isGroupManager, originalMembers]);

  return (
    <Stack gap="0">
      <MembersList
        hideMore={hideMore}
        members={originalMembers}
        onMemberAvatarClick={onMemberAvatarClick}
        closeAllClick={closeAllClick}
      />
      <Card>
        <Stack gap="2">
          <Flex alignItems="center" gap="2.5" cursor="pointer" onClick={onAddMembers}>
            <Center fontSize="9xl">
              <AddIcon />
            </Center>
            <Box flex="1" py="2.5" borderBottom="1px solid" borderColor="border.subtle">
              <Text fontSize="sm" fontWeight="mediumBold">
                {t('action.addMembers')}
              </Text>
            </Box>
          </Flex>
          {isGroupManager && (
            <Flex alignItems="center" gap="2.5" cursor="pointer" onClick={onRemoveMembers}>
              <Center fontSize="9xl">
                <RemoveIcon />
              </Center>
              <Box flex="1" py="2.5" borderBottom="1px solid" borderColor="border.subtle">
                <Text fontSize="sm" fontWeight="mediumBold">
                  {t('action.removeMembers')}
                </Text>
              </Box>
            </Flex>
          )}
          <Box ml="-2">
            {showMembers.map((member, index) => (
              <UserItem
                key={member.user_id}
                {...member}
                rippleEffect={false}
                hoverable={false}
                onAvatarClick={() => onMemberAvatarClick?.(member)}
                userExtraElement={
                  <>
                    {member.state === StateEnum.DELETE_ACCOUNT ? null : (
                      <>
                        {member.role === 1 && (
                          <Center fontSize="xs" fontWeight="bold">
                            {/* <OWIcon /> */}
                            <Text color="brand.100" wordBreak="keep-all">
                              {t('chat.groupRoleOwner')}
                            </Text>
                          </Center>
                        )}
                        {member.role === 2 && (
                          <Center fontSize="xs" fontWeight="bold">
                            {/* <MAIcon /> */}
                            <Text color="fg.muted" wordBreak="keep-all">
                              {t('chat.groupRoleAdmin')}
                            </Text>
                          </Center>
                        )}
                      </>
                    )}
                  </>
                }
                borderColor={index === showMembers.length - 1 ? 'transparent' : 'border.subtle'}
              />
            ))}
          </Box>
        </Stack>
      </Card>
    </Stack>
  );
};

const MemoizedMembersCard = memo(MembersCard);
export { MemoizedMembersCard as MembersCard };
